import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { OrderCard } from '@/components/orders/OrderCard';
import { EmptyState } from '@/components/dashboard/EmptyState';
import { Package, Filter, AlertCircle, Star, CalendarIcon } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useVehicleTypes } from '@/hooks/useVehicleTypes';
import { useTransportJobs } from '@/hooks/useTransportJobs';
import { toast } from 'sonner';
import { BidDialog } from '@/components/orders/BidDialog';
import { useTranslation } from 'react-i18next';
import { geocodeAddress } from '@/lib/geocoding';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { fr, enUS, es, de, it } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const TransporterMarketplace = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { vehicleTypes, getVehicleTypeName } = useVehicleTypes();
  const { createJob, isCreating } = useTransportJobs();
  
  const [trailerTypeFilter, setTrailerTypeFilter] = useState<string>('all');
  const [departureCity, setDepartureCity] = useState<string>('');
  const [departureRadius, setDepartureRadius] = useState<string>('50');
  const [arrivalCity, setArrivalCity] = useState<string>('');
  const [arrivalRadius, setArrivalRadius] = useState<string>('50');
  const [availabilityDate, setAvailabilityDate] = useState<Date | undefined>(undefined);
  
  // Geocoded coordinates cache
  const [departureCoordsCache, setDepartureCoordsCache] = useState<{ lat: number; lng: number } | null>(null);
  const [arrivalCoordsCache, setArrivalCoordsCache] = useState<{ lat: number; lng: number } | null>(null);

  const getDateLocale = () => {
    switch (i18n.language) {
      case 'fr': return fr;
      case 'es': return es;
      case 'de': return de;
      case 'it': return it;
      default: return enUS;
    }
  };

  // Fonction pour calculer la distance en km entre deux points (formule de Haversine)
  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // Rayon de la Terre en km
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  };

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['marketplace-orders', trailerTypeFilter, departureCoordsCache, departureRadius, arrivalCoordsCache, arrivalRadius, availabilityDate],
    queryFn: async () => {
      let query = supabase
        .from('orders')
        .select(`
          *,
          bids!left(id, is_accepted)
        `)
        .eq('marketplace_visible', true)
        .eq('status', 'posted')
        .is('bids.is_accepted', null)
        .order('created_at', { ascending: false });

      if (trailerTypeFilter !== 'all') {
        query = query.eq('trailer_type', trailerTypeFilter as any);
      }

      const { data, error } = await query;
      if (error) throw error;
      
      let filteredData = data || [];
      
      // Filtrer par ville de départ + rayon
      if (departureCoordsCache && departureRadius) {
        const maxDistKm = parseFloat(departureRadius);
        filteredData = filteredData.filter(order => {
          if (!order.pickup_lat || !order.pickup_lng) return false;
          const distance = calculateDistance(
            departureCoordsCache.lat,
            departureCoordsCache.lng,
            order.pickup_lat,
            order.pickup_lng
          );
          return distance <= maxDistKm;
        });
      }
      
      // Filtrer par ville d'arrivée + rayon
      if (arrivalCoordsCache && arrivalRadius) {
        const maxDistKm = parseFloat(arrivalRadius);
        filteredData = filteredData.filter(order => {
          if (!order.delivery_lat || !order.delivery_lng) return false;
          const distance = calculateDistance(
            arrivalCoordsCache.lat,
            arrivalCoordsCache.lng,
            order.delivery_lat,
            order.delivery_lng
          );
          return distance <= maxDistKm;
        });
      }
      
      // Filtrer par date de disponibilité
      if (availabilityDate) {
        const filterDate = new Date(availabilityDate);
        filterDate.setHours(0, 0, 0, 0);
        filteredData = filteredData.filter(order => {
          const pickupDate = new Date(order.pickup_date);
          pickupDate.setHours(0, 0, 0, 0);
          return pickupDate >= filterDate;
        });
      }
      
      // Trier par date de pickup
      filteredData = [...filteredData].sort((a, b) => {
        return new Date(a.pickup_date).getTime() - new Date(b.pickup_date).getTime();
      });
      
      return filteredData;
    },
  });

  // Récupérer les offres du transporteur
  const { data: myBids = [] } = useQuery({
    queryKey: ['my-bids'],
    queryFn: async () => {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData?.user) return [];

      const { data, error } = await supabase
        .from('bids')
        .select('*')
        .eq('transporter_user_id', userData.user.id);

      if (error) throw error;
      return data;
    },
  });

  // Récupérer les ratings des transporteurs qui ont fait des bids
  const { data: companyRatings = [] } = useQuery({
    queryKey: ['marketplace-company-ratings', myBids],
    queryFn: async () => {
      if (!myBids.length) return [];
      
      const companyIds = [...new Set(myBids.map(bid => bid.transporter_company_id))];
      
      const { data, error } = await supabase
        .from('company_ratings_summary')
        .select('*')
        .in('company_id', companyIds);

      if (error) throw error;
      return data || [];
    },
    enabled: myBids.length > 0,
  });

  // Créer un map des bids par order_id pour un accès rapide
  const bidsByOrderId = myBids.reduce((acc: any, bid: any) => {
    if (!acc[bid.order_id]) {
      acc[bid.order_id] = [];
    }
    acc[bid.order_id].push(bid);
    return acc;
  }, {});

  // Fonction pour vérifier si une offre a été refusée pour une commande
  const getDeclinedBid = (orderId: string) => {
    const bids = bidsByOrderId[orderId] || [];
    return bids.find((bid: any) => bid.declined_at !== null);
  };

  // Fonction pour obtenir l'offre active (non refusée, non acceptée)
  const getActiveBid = (orderId: string) => {
    const bids = bidsByOrderId[orderId] || [];
    return bids.find((bid: any) => bid.declined_at === null && !bid.is_accepted);
  };

  // Fonction pour obtenir le rating d'un transporteur
  const getTransporterRating = (transporterCompanyId: string) => {
    return companyRatings.find(r => r.company_id === transporterCompanyId);
  };

  const handleBookOrder = async (orderId: string) => {
    try {
      await createJob({ orderId });
      toast.success(t('marketplace.messages.applicationSent'));
      // Rafraîchir la liste des commandes du marketplace
      queryClient.invalidateQueries({ queryKey: ['marketplace-orders'] });
    } catch (error: any) {
      toast.error(error.message || t('marketplace.errors.applicationError'));
    }
  };

  // Geocoder la ville de départ
  const handleDepartureCityBlur = async () => {
    if (!departureCity.trim()) {
      setDepartureCoordsCache(null);
      return;
    }
    const result = await geocodeAddress(departureCity);
    if ('latitude' in result) {
      setDepartureCoordsCache({ lat: result.latitude, lng: result.longitude });
    } else {
      setDepartureCoordsCache(null);
    }
  };

  // Geocoder la ville d'arrivée
  const handleArrivalCityBlur = async () => {
    if (!arrivalCity.trim()) {
      setArrivalCoordsCache(null);
      return;
    }
    const result = await geocodeAddress(arrivalCity);
    if ('latitude' in result) {
      setArrivalCoordsCache({ lat: result.latitude, lng: result.longitude });
    } else {
      setArrivalCoordsCache(null);
    }
  };

  const handleResetFilters = () => {
    setTrailerTypeFilter('all');
    setDepartureCity('');
    setDepartureRadius('50');
    setArrivalCity('');
    setArrivalRadius('50');
    setAvailabilityDate(undefined);
    setDepartureCoordsCache(null);
    setArrivalCoordsCache(null);
  };

  return (
    <div className="space-y-8">
      <div className="max-w-7xl mx-auto">
        <DashboardHeader 
          title="Marketplace"
          userName={user?.name || user?.email}
        />

        {/* Filters Card */}
        <Card className="mb-8 rounded-xl shadow-md">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="text-[#0880AE]" size={20} />
              <h3 className="text-lg font-medium text-[#37699E] font-heading">{t('marketplace.filters')}</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {/* Type de remorque */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                  {t('marketplace.trailerType')}
                </Label>
                <Select value={trailerTypeFilter} onValueChange={setTrailerTypeFilter}>
                  <SelectTrigger className="rounded-lg border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
                    <SelectValue placeholder={t('marketplace.allTypes')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('marketplace.allTypes')}</SelectItem>
                    {vehicleTypes.map((type) => (
                      <SelectItem key={type.id} value={type.trailer_type}>
                        {getVehicleTypeName(type.trailer_type)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Ville de départ + rayon */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                  {t('marketplace.departureCity')}
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder={t('marketplace.departureCityPlaceholder')}
                    value={departureCity}
                    onChange={(e) => setDepartureCity(e.target.value)}
                    onBlur={handleDepartureCityBlur}
                    className="flex-1 rounded-lg border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20"
                  />
                  <Select value={departureRadius} onValueChange={setDepartureRadius}>
                    <SelectTrigger className="w-28 rounded-lg border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">±20 km</SelectItem>
                      <SelectItem value="50">±50 km</SelectItem>
                      <SelectItem value="100">±100 km</SelectItem>
                      <SelectItem value="200">±200 km</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Ville d'arrivée + rayon */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                  {t('marketplace.arrivalCity')}
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    placeholder={t('marketplace.arrivalCityPlaceholder')}
                    value={arrivalCity}
                    onChange={(e) => setArrivalCity(e.target.value)}
                    onBlur={handleArrivalCityBlur}
                    className="flex-1 rounded-lg border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20"
                  />
                  <Select value={arrivalRadius} onValueChange={setArrivalRadius}>
                    <SelectTrigger className="w-28 rounded-lg border-[#DBE2EA] focus:border-[#0880AE] focus:ring-2 focus:ring-[#0880AE]/20">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="20">±20 km</SelectItem>
                      <SelectItem value="50">±50 km</SelectItem>
                      <SelectItem value="100">±100 km</SelectItem>
                      <SelectItem value="200">±200 km</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Date de disponibilité */}
              <div className="space-y-2">
                <Label className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide">
                  {t('marketplace.availabilityDate')}
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn(
                        "w-full justify-start text-left font-normal rounded-lg border-[#DBE2EA]",
                        !availabilityDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {availabilityDate ? (
                        format(availabilityDate, 'PPP', { locale: getDateLocale() })
                      ) : (
                        <span>{t('marketplace.selectDate')}</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={availabilityDate}
                      onSelect={setAvailabilityDate}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                      locale={getDateLocale()}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Bouton reset */}
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={handleResetFilters}
                  className="w-full border-[#37699E] text-[#37699E] hover:bg-[#EBF4F8] rounded-lg"
                >
                  {t('marketplace.resetFilters')}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Orders List */}
        {isLoading ? (
          <div className="text-center py-12">
            <p className="text-[#5A6B7A]">{t('common.loading')}</p>
          </div>
        ) : orders.length === 0 ? (
          <EmptyState
            message={t('marketplace.noOrders')}
            icon={Package}
          />
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {orders.map((order) => (
              <Card key={order.id} className="rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-xl font-medium text-[#37699E] font-heading mb-2">
                        {order.cargo_title || order.cargo_description}
                      </h3>
                      <p className="text-sm text-[#5A6B7A] mb-4">{order.order_number}</p>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide mb-1">{t('marketplace.card.pickup')}</p>
                          <p className="text-sm text-[#2D3748]">{order.pickup_address}</p>
                          <p className="text-sm text-[#5A6B7A]">{new Date(order.pickup_date).toLocaleDateString(i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : i18n.language === 'de' ? 'de-DE' : i18n.language === 'it' ? 'it-IT' : 'fr-FR')}</p>
                        </div>
                        <div>
                          <p className="text-xs font-medium text-[#5A6B7A] uppercase tracking-wide mb-1">{t('marketplace.card.delivery')}</p>
                          <p className="text-sm text-[#2D3748]">{order.delivery_address}</p>
                          <p className="text-sm text-[#5A6B7A]">{new Date(order.delivery_date).toLocaleDateString(i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : i18n.language === 'de' ? 'de-DE' : i18n.language === 'it' ? 'it-IT' : 'fr-FR')}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 mb-4 flex-wrap">
                        <span className="text-sm text-[#5A6B7A]">
                          <span className="font-medium">{t('marketplace.card.weight')}</span> {order.weight_kg} kg
                        </span>
                        {order.pallets_count && (
                          <span className="text-sm text-[#5A6B7A]">
                            <span className="font-medium">{t('marketplace.card.pallets')}</span> {order.pallets_count}
                          </span>
                        )}
                        {(!order.pickup_lat || !order.delivery_lat) && (
                          <Badge variant="outline" className="text-[#F2AC57] border-[#F2AC57]">
                            <AlertCircle className="w-3 h-3 mr-1" />
                            {t('orders.noCoordinates')}
                          </Badge>
                        )}
                      </div>

                      {order.proposed_price && (
                        <div className="text-2xl font-semibold text-[#37699E] font-heading mb-4">
                          {order.proposed_price} €
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-3">
                    {getDeclinedBid(order.id) && (
                      <div className="flex items-start gap-2 p-3 rounded-lg bg-[#FF7171]/10 border border-[#FF7171]/20">
                        <AlertCircle className="text-[#FF7171] flex-shrink-0 mt-0.5" size={18} />
                        <div className="flex-1">
                          <p className="text-sm text-[#2D3748] font-medium">{t('marketplace.card.declinedBid')}</p>
                          <p className="text-xs text-[#5A6B7A] mt-1">
                            {t('marketplace.card.declinedAmount')} {getDeclinedBid(order.id).amount} € • 
                            {t('marketplace.card.declinedOn')} {new Date(getDeclinedBid(order.id).declined_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : i18n.language === 'de' ? 'de-DE' : i18n.language === 'it' ? 'it-IT' : 'fr-FR')}
                          </p>
                        </div>
                      </div>
                    )}

                    {getActiveBid(order.id) && (
                      <div className="p-4 rounded-lg bg-[#0880AE]/10 border border-[#0880AE]/20">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-sm font-medium text-[#37699E]">{t('marketplace.card.pendingBid')}</p>
                          {(() => {
                            const rating = getTransporterRating(getActiveBid(order.id).transporter_company_id);
                            return rating && rating.as_transporter_average_rating ? (
                              <div className="flex items-center gap-1 bg-white rounded-lg px-2 py-1 shadow-sm">
                                <Star className="text-[#FFBF07] fill-[#FFBF07]" size={14} />
                                <span className="text-sm font-semibold text-[#37699E]">
                                  {rating.as_transporter_average_rating.toFixed(1)}
                                </span>
                                <span className="text-xs text-[#5A6B7A]">
                                  ({rating.as_transporter_total_ratings})
                                </span>
                              </div>
                            ) : null;
                          })()}
                        </div>
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs text-[#5A6B7A] uppercase tracking-wide font-medium">{t('marketplace.card.proposedAmount')}</p>
                            <p className="text-2xl font-semibold text-[#37699E] font-['Lexend_Deca']">
                              {getActiveBid(order.id).amount} €
                            </p>
                          </div>
                          {getActiveBid(order.id).message && (
                            <div>
                              <p className="text-xs text-[#5A6B7A] uppercase tracking-wide font-medium">{t('marketplace.card.message')}</p>
                              <p className="text-sm text-[#2D3748]">{getActiveBid(order.id).message}</p>
                            </div>
                          )}
                          <p className="text-xs text-[#5A6B7A]">
                            {t('marketplace.card.submittedOn')} {new Date(getActiveBid(order.id).created_at).toLocaleDateString(i18n.language === 'en' ? 'en-US' : i18n.language === 'es' ? 'es-ES' : i18n.language === 'de' ? 'de-DE' : i18n.language === 'it' ? 'it-IT' : 'fr-FR')}
                          </p>
                        </div>
                      </div>
                    )}
                    
                    <div className="flex gap-3">
                      {order.proposed_price ? (
                        <Button
                          onClick={() => handleBookOrder(order.id)}
                          disabled={isCreating}
                          className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg"
                        >
                          {isCreating ? t('marketplace.card.applying') : t('marketplace.card.applyForOrder')}
                        </Button>
                      ) : (
                        <BidDialog orderId={order.id}>
                          <Button 
                            className="bg-[#FFBF07] hover:bg-[#E5AB06] text-[#2D3748] font-semibold rounded-lg"
                            disabled={!!getActiveBid(order.id)}
                          >
                            {getActiveBid(order.id) 
                              ? t('marketplace.card.bidPending')
                              : getDeclinedBid(order.id) 
                              ? t('marketplace.card.makeNewBid')
                              : t('marketplace.card.makeBid')}
                          </Button>
                        </BidDialog>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TransporterMarketplace;
